import { ErrorRule } from '../types';

/**
 * Built-in, read-only ruleset presets shipped with the editor
 * (ERROR-HANDLING-UI-PLAN §14). Selecting a preset SEEDS an editable project
 * ruleset (or an inline one) — presets are never referenced at runtime.
 *
 * The two implicit engine defaults (transient → retry ∞, then else → throw the
 * surfaced code) are always appended by the engine, so presets only author the
 * rules that differ from the defaults.
 */
export interface RuleSetPreset {
  key: string;
  name: string;
  description: string;
  rules: ErrorRule[];
}

export const ERROR_RULESET_PRESETS: RuleSetPreset[] = [
  {
    key: 'REST-Standard',
    name: 'REST-Standard',
    description: 'General REST call: business 4xx map to distinct error boundaries; infra/throttle rides out.',
    rules: [
      { match: { statusCodes: [409] }, action: { disposition: 'throw', errorCode: 'Conflict' } },
      { match: { statusCodes: [401, 403] }, action: { disposition: 'throw', errorCode: 'AuthFailed' } },
      { match: { statusCodes: [400, 422] }, action: { disposition: 'throw', errorCode: 'BadRequest' } },
      { match: { statusCodes: [404] }, action: { disposition: 'throw', errorCode: 'NotFound' } },
      { match: { statusCodes: [429] }, action: { disposition: 'retry', maxAttempts: 0 } },
      { match: { transient: true }, action: { disposition: 'retry', maxAttempts: 0 } },
    ],
  },
  {
    key: 'REST-FailFast',
    name: 'REST-FailFast',
    description: 'Critical task: surface errors quickly with a bounded retry, then fault.',
    rules: [
      { match: { statusCodes: [401, 403] }, action: { disposition: 'throw', errorCode: 'AuthFailed' } },
      {
        match: { transient: true },
        action: {
          disposition: 'retry',
          maxAttempts: 3,
          onExhausted: { disposition: 'throw', errorCode: 'Unavailable' },
        },
      },
    ],
  },
  {
    key: 'DB-Contention',
    name: 'DB-Contention',
    description: 'Database write under lock contention: retry deadlocks/lock-waits finitely, then surface.',
    rules: [
      {
        match: { statusCodes: [1205, 1213, '40001', '40P01'] },
        action: {
          disposition: 'retry',
          maxAttempts: 8,
          backoff: { baseMs: 200, factor: 3, capMs: 30000, jitter: 0.25 },
          onExhausted: { disposition: 'throw', errorCode: 'DbContention' },
        },
      },
      { match: { transient: true }, action: { disposition: 'retry', maxAttempts: 0 } },
    ],
  },
  {
    key: 'Best-Effort-Notify',
    name: 'Best-Effort-Notify',
    description: 'Email / SMS / push notification whose failure must NOT fault the process.',
    rules: [
      {
        match: { bodyPredicate: { kind: 'regex', expression: 'mailbox.*full|over.?quota|recipient.*reject', op: 'matches' } },
        action: { disposition: 'ignore', captureVariable: 'notifyError' },
      },
      {
        match: { transient: true },
        action: {
          disposition: 'retry',
          maxAttempts: 10,
          onExhausted: { disposition: 'ignore', captureVariable: 'notifyError' },
        },
      },
      { match: {}, action: { disposition: 'ignore', captureVariable: 'notifyError' } },
    ],
  },
  {
    key: 'Idempotent-Reconcile',
    name: 'Idempotent-Reconcile',
    description: 'Idempotent upsert/reconcile: already-exists / not-found are normal outcomes to swallow.',
    rules: [
      { match: { statusCodes: [409] }, action: { disposition: 'ignore', captureVariable: 'priorState' } },
      { match: { statusCodes: [404] }, action: { disposition: 'ignore' } },
      { match: { transient: true }, action: { disposition: 'retry', maxAttempts: 0 } },
    ],
  },
  {
    key: 'Agent-Tolerant',
    name: 'Agent-Tolerant',
    description: 'LLM / agent step: tolerate provider hiccups and never block the flow.',
    rules: [
      { match: { statusCodes: [429] }, action: { disposition: 'retry', maxAttempts: 0 } },
      {
        match: { bodyPredicate: { kind: 'jsonpath', expression: '$.error.code', op: 'equals', value: 'content_filter' } },
        action: { disposition: 'throw', errorCode: 'ContentFiltered' },
      },
      {
        match: { statusCodes: ['500-599'] },
        action: {
          disposition: 'retry',
          maxAttempts: 6,
          onExhausted: { disposition: 'ignore', captureVariable: 'agentError' },
        },
      },
      { match: {}, action: { disposition: 'ignore', captureVariable: 'agentError' } },
    ],
  },
  {
    key: 'Never-Lose-Transient',
    name: 'Never-Lose-Transient',
    description: 'Exactly the engine defaults, made explicit and editable as a starting point.',
    rules: [
      { match: { transient: true }, action: { disposition: 'retry', maxAttempts: 0 } },
    ],
  },
];

export const getPreset = (key: string): RuleSetPreset | undefined =>
  ERROR_RULESET_PRESETS.find((p) => p.key === key);
